import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';

const ApprovalApplication = () => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: '',
      businessName: '',
      currentEpos: '',
      venueCount: '',
    },
  });

  const [error, setError] = React.useState('');

  const onSubmit = async (data) => {
    setError('');
    try {
      // In future this is where you'll POST to your backend / Mongo.
      navigate('/pending-approval', { state: data });
    } catch (err) {
      setError('Something went wrong while saving your application. Please try again.');
    }
  };

  const logoUrl =
    'https://horizons-cdn.hostinger.com/f34f6b4a-5701-45b6-9778-48a500b88e4d/00c7de63c68d9d1ea880547b4c0c04a6.png';

  const cleanField = {
    mb: 3,
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '& fieldset': {
        borderColor: 'transparent',
      },
      '&:hover fieldset': {
        borderColor: 'transparent',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'transparent',
        boxShadow: '0 0 0 2px rgba(0,0,0,0.15)',
      },
      transition: 'all 0.3s ease',
      background: 'rgba(255,255,255,0.85)',
    },
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#ffffff',
        animation: 'fadeIn 0.8s ease',
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{ height: '80vh', display: 'flex', alignItems: 'center' }}
      >
        <Grid
          item
          xs={12}
          md={7}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            animation: 'slideUp 0.7s ease',
          }}
        >
          <Paper
            elevation={4}
            sx={{
              p: 4,
              width: '100%',
              maxWidth: 600,
              borderRadius: 3,
              background: 'rgba(255,255,255,0.65)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 8px 28px rgba(0,0,0,0.15)',
            }}
          >
            <Box sx={{ mb: 3, textAlign: 'left' }}>
              <img
                src={logoUrl}
                alt="RTNZERO"
                style={{
                  width: 120,
                  marginBottom: 12,
                }}
              />
              <Typography variant="h5" fontWeight="bold" color="black" gutterBottom>
                Quick details to get started
              </Typography>
              <Typography variant="body2" color="gray">
                This isn&apos;t a contract or long onboarding – it just helps us understand your
                business so we can set up the right RTNZERO experience for you.
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First name"
                    {...register('firstName', { required: 'First name is required' })}
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    sx={cleanField}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last name"
                    {...register('lastName', { required: 'Last name is required' })}
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    sx={cleanField}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    sx={cleanField}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Role (Owner, Manager, etc.)"
                    {...register('role', { required: 'Role is required' })}
                    error={!!errors.role}
                    helperText={errors.role?.message}
                    sx={cleanField}
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                label="Business name"
                {...register('businessName', { required: 'Business name is required' })}
                error={!!errors.businessName}
                helperText={errors.businessName?.message}
                sx={cleanField}
              />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Current EPOS (if any)"
                    placeholder="e.g. Square, Lightspeed, Zettle"
                    {...register('currentEpos')}
                    error={!!errors.currentEpos}
                    helperText={errors.currentEpos?.message}
                    sx={cleanField}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Number of venues"
                    type="number"
                    {...register('venueCount', {
                      required: 'Number of venues is required',
                      min: { value: 1, message: 'At least one venue is required' },
                    })}
                    error={!!errors.venueCount}
                    helperText={errors.venueCount?.message}
                    sx={cleanField}
                  />
                </Grid>
              </Grid>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isSubmitting}
                sx={{
                  mt: 1,
                  mb: 1,
                  py: 1.5,
                  borderRadius: 2,
                  background: 'black',
                  color: 'white',
                  '&:hover': {
                    background: '#333',
                  },
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit for Approval'}
              </Button>

              <Typography variant="caption" color="gray" sx={{ display: 'block', mt: 2 }}>
                That&apos;s it for now – once you hit submit, our team will review your request and
                be in touch to talk through next steps and give you access.
              </Typography>
            </form>
          </Paper>
        </Grid>

        {/* Right column: reserved for media / brand storytelling */}
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'stretch',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              borderRadius: 3,
              background:
                'radial-gradient(circle at top left, #ecfeff 0%, #f5f3ff 40%, #f9fafb 100%)',
            }}
          />
        </Grid>
      </Grid>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { transform: translateY(25px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}
      </style>
    </Container>
  );
};

export default ApprovalApplication;

